import { createSlice } from "@reduxjs/toolkit";
// utils
import axios, { API_ENDPOINTS } from "src/utils/axios";
import { downloadURL } from "src/utils/handle-task";

// ----------------------------------------------------------------------

const initialState = {
    isLoading: false,
    isPopoverLoading: false,
    error: null,
    //
    tasksPopover: [],
    //
    tasks: [],
    taskFilter: {
        biz_type: "all",
        start_day: "",
        end_day: "",
        page: 1,
        page_size: 10,
        total: 0
    },
    totalUnFinished: 0
};

const slice = createSlice({
    name: "task",
    initialState,
    reducers: {
        // START LOADING
        startLoading(state) {
            state.isLoading = true;
        },

        // STOP LOADING
        stopLoading(state) {
            state.isLoading = false;
        },

        // START LOADING POPOVER
        startLoadingPopover(state) {
            state.isPopoverLoading = true;
        },

        // STOP LOADING POPOVER
        stopLoadingPopover(state) {
            state.isPopoverLoading = false;
        },

        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.isPopoverLoading = false;
            state.error = action.payload;
        },

        clearError(state) {
            state.error = null;
        },

        // GET TASK POPOVER LIST
        getTasksPopoverSuccess(state, action) {
            state.isPopoverLoading = false;
            state.tasksPopover = action.payload;
        },

        // GET TASK LIST
        getTasksSuccess(state, action) {
            state.isLoading = false;
            state.tasks = action.payload;
        },

        // UPDATE TASK FILTER
        updateTaskListFilterSuccess(state, action) {
            state.taskFilter = {
                ...state.taskFilter,
                ...action.payload
            };
        },

        // GET TOTAL UNREAD MESSAGE NUMBER
        getTaskUnFinishedCountSuccess(state, action) {
            state.totalUnFinished = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

// Actions
export const { clearError, getTaskUnFinishedCountSuccess } = slice.actions;

// ----------------------------------------------------------------------

const filterEmptyObjValues = (obj) => {
    const result = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
        if (obj[key] !== "" && obj[key] !== "all" && !( Array.isArray(obj[key]) && obj[key].length === 0 )) {
            result[key] = obj[key];
        }
    }

    switch (result.biz_type) {
        case "all":
            result.biz_type = "";
            break;
        case "SYNC":
            result.biz_type = "ORDER_SYNC";
            break;
        case "IMPORT":
            result.biz_type = "ORDER_IMPORT";
            break;
        case "EXPORT":
            result.biz_type = "TRANSACTION_RECORD_EXPORT,SHIPMENT_RECORD_EXPORT,BATCH_DOWNLOAD_PACKING_SLIP,SHIPMENT_BILLING_EXPORT";
            break;
        case "SHIPPING_RULE":
            result.biz_type = "APPLY_SHIPPING_RULE";
            break;
        default:
            result.biz_type = "";
            break;
    }

    if (!result.biz_type) {
        delete result.biz_type;
    }

    delete result.total;

    return result;
};

// ----------------------------------------------------------------------

export function getTaskPopoverList() {
    return async (dispatch) => {
        dispatch(slice.actions.startLoadingPopover());

        try {
            const response = await axios.get(API_ENDPOINTS.tasks.getTasks, {
                params: {
                    page: 1,
                    page_size: 3
                }
            });

            const { status, data } = response;

            if (status === 200 && data?.code === "ok") {
                dispatch(slice.actions.getTasksPopoverSuccess(data?.data?.tasks || []));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

export function getTaskList(filter) {
    return async (dispatch, getState) => {
        dispatch(slice.actions.startLoading());

        try {
            const localFilter = { ...( filter || { ...getState().task.taskFilter } ) };

            const response = await axios.get(API_ENDPOINTS.tasks.getTasks, {
                params: filterEmptyObjValues(localFilter)
            });

            const { status, data } = response;

            if (status === 200 && data?.code === "ok") {
                dispatch(slice.actions.getTasksSuccess(data?.data?.tasks || []));
                dispatch(slice.actions.updateTaskListFilterSuccess({
                    total: data?.data?.count || 0
                }));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

// ----------------------------------------------------------------------

export function updateTaskListFilter(filter) {
    return async (dispatch, getState) => {
        const { taskFilter } = getState().task;

        dispatch(slice.actions.updateTaskListFilterSuccess({
            biz_type: filter?.biz_type ?? taskFilter.biz_type,
            start_day: filter?.start_day ?? taskFilter.start_day,
            end_day: filter?.end_day ?? taskFilter.end_day,
            page: filter?.page ?? taskFilter.page,
            page_size: filter?.page_size ?? taskFilter.page_size,
            total: filter?.total ?? taskFilter.total
        }));
    };
}

// ----------------------------------------------------------------------

export function resetTaskFilter() {
    return async (dispatch) => {
        const defaultFilter = {
            biz_type: "all",
            start_day: "",
            end_day: "",
            page: 1,
            page_size: 10,
            total: 0
        };

        dispatch(slice.actions.updateTaskListFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

// ----------------------------------------------------------------------

export function checkTaskById(ids) {
    return async (dispatch) => {
        try {
            const response = await axios.get(API_ENDPOINTS.tasks.queryById, {
                params: {
                    task_ids: ids.join(",")
                }
            });

            const { status, data } = response;

            if (status === 200 && data?.code === "ok") {
                return data?.data ?? {};
            }

            return {};
        } catch (e) {
            dispatch(slice.actions.hasError(e));

            return {};
        }
    };
}

// ----------------------------------------------------------------------

export function recursiveCheckTaskById(id, count, snackbar) {
    return async (dispatch) => {
        try {
            const result = await dispatch(checkTaskById([id]));

            const task = result[id];

            if (task) {
                if (task?.status === "SUCCESS") {
                    dispatch(slice.actions.getTaskUnFinishedCountSuccess(0));

                    if (snackbar) {
                        const type = task.biz_type || task.task_type;

                        switch (type) {
                            case "ORDER_SYNC":
                                snackbar("Sync Order success！The list is currently updated", { variant: "success" });
                                break;
                            case "ORDER_IMPORT":
                                snackbar("Order Import success！", { variant: "success" });
                                break;
                            case "BATCH_DOWNLOAD_PACKING_SLIP":
                                snackbar("Packing Slip download success！", { variant: "success" });
                                break;
                            case "TRANSACTION_RECORD_EXPORT":
                            case "SHIPMENT_RECORD_EXPORT":
                            case "SHIPMENT_BILLING_EXPORT":
                            case "EXPORT":
                                snackbar("Export success！You can see it in the download list", { variant: "success" });
                                break;
                            case "APPLY_SHIPPING_RULE":
                                snackbar("Apply Shipping Rule success！", { variant: "success" });
                                break;
                            default:
                                break;
                        }
                    }

                    if (task?.export_url) {
                        downloadURL(task.export_url);
                    } else if (task?.result) {
                        try {
                            const { url } = JSON.parse(task.result) || {};

                            if (url) {
                                downloadURL(url);
                            }
                        } catch (e) {
                            // ignore malformed result payload
                        }
                    }
                } else if (task?.status === "FAILED") {
                    if (snackbar) {
                        const type = task.biz_type || task.task_type;

                        switch (type) {
                            case "ORDER_SYNC":
                                snackbar(`Sync Order failure！ ${task?.fail_reason}`, { variant: "error" });
                                break;
                            case "ORDER_IMPORT":
                                snackbar(`Order Import failure！ ${task?.fail_reason}`, { variant: "error" });
                                break;
                            case "BATCH_DOWNLOAD_PACKING_SLIP":
                                snackbar(`Packing Slip download failure！ ${task?.fail_reason}`, { variant: "error" });
                                break;
                            case "TRANSACTION_RECORD_EXPORT":
                            case "SHIPMENT_RECORD_EXPORT":
                            case "SHIPMENT_BILLING_EXPORT":
                            case "EXPORT":
                                snackbar(`Export failure！ ${task?.fail_reason}`, { variant: "error" });
                                break;
                            case "APPLY_SHIPPING_RULE":
                                snackbar(`Apply Shipping Rule failure！ ${task?.fail_reason}`, { variant: "error" });
                                break;
                            default:
                                break;
                        }
                    }

                    dispatch(slice.actions.getTaskUnFinishedCountSuccess(0));
                } else if (task?.status === "PROCESSING") {
                    if (count < 51) {
                        dispatch(slice.actions.getTaskUnFinishedCountSuccess(1));

                        setTimeout(() => {
                            dispatch(recursiveCheckTaskById(id, count + 1, snackbar));
                        }, 2000);
                    } else {
                        // 超过设置检测次数
                    }
                }
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

// ----------------------------------------------------------------------

export function getUnreadTaskNum() {
    return async (dispatch) => {
        try {
            const response = await axios.get(API_ENDPOINTS.tasks.getTasks, {
                params: {
                    status: "PROCESSING",
                    page: 1,
                    page_size: 1
                }
            });

            const { status, data } = response;

            if (status === 200) {
                dispatch(slice.actions.getTaskUnFinishedCountSuccess(data?.data?.count ?? 0));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}
